/**
 * FormField + FormSection — the small building blocks the two modal shapes
 * compose. FormField is a labelled control cell (`.mfield`) with an optional
 * required marker, unit suffix, and inline error. FormSection is a
 * `.subhead`-divided group wrapping a `.mform` / `.mform.three` grid — the
 * section divider used by the wide grouped modal (§27).
 */
import type { ReactNode } from "react";

export interface FormFieldProps {
  label: ReactNode;
  required?: boolean;
  /** Unit suffix rendered to the right of the control (ft, m, ppg, …). */
  unit?: ReactNode;
  /** Inline validation error. */
  error?: ReactNode;
  children: ReactNode;
  /** Data-lineage owner tag (admin overlay). */
  owner?: "m" | "p" | "z" | "u";
}

export function FormField({ label, required, unit, error, children, owner }: FormFieldProps) {
  return (
    <div className="mfield" data-own={owner}>
      <label className={required ? "req" : undefined}>{label}</label>
      {unit ? (
        <div className="withunit">
          {children}
          <span className="unit">{unit}</span>
        </div>
      ) : (
        children
      )}
      {error ? <span className="err">{error}</span> : null}
    </div>
  );
}

export interface FormSectionProps {
  title: ReactNode;
  /** Optional right-aligned helper text on the divider. */
  note?: ReactNode;
  /** 3-column layout (wide grouped modal) vs default 2-column. */
  three?: boolean;
  children: ReactNode;
}

/** A `.subhead` divider + a `.mform` grid of the section's fields. */
export function FormSection({ title, note, three, children }: FormSectionProps) {
  return (
    <>
      <div className="subhead">
        <h3>{title}</h3>
        <span className="ln" />
        {note ? <span className="dim">{note}</span> : null}
      </div>
      <div className={`mform${three ? " three" : ""}`}>{children}</div>
    </>
  );
}
